import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { LinkBox, LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { BodyM, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { LightedBezel } from '@sorare/core/src/atoms/ui/bezels/LightedBezel';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { ClaimButton } from '#src/components/missions/UnclaimedTask/ClaimButton/index.tsx';

import type { GenericTaskRow_taskInterface } from './__generated__/index.graphql.ts';

const InProgressWrapper = styled(LinkBox)`
  width: 100%;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);
  background: var(--c-nl-800);
  text-align: left;
  position: relative;
`;

const ClaimableContainer = styled.div`
  width: 100%;
  position: relative;
  &.claimed {
    opacity: 0.5;
  }
  transition: all 0.2s ease-in-out;
`;

const StyledLightedBezel = styled(LightedBezel)`
  width: 100%;
`;

const CardContent = styled(Horizontal).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--double-unit);
  position: relative;
`;

const ContentWrapper = styled(Vertical).attrs({ gap: 1 })`
  flex: 1;
  align-items: flex-start;
`;

const ProgressSection = styled(Horizontal).attrs({ gap: 1 })`
  flex: 1;
  align-items: center;
  min-width: 0;
`;

const StyledProgress = styled(Progress)`
  flex: 1;
  min-width: 0;
`;

type Props = {
  task: GenericTaskRow_taskInterface;
  onClaim?: () => void;
  loading?: boolean;
};

export const GenericTaskRow = ({ task, onClaim, loading }: Props) => {
  const { title, description, progress, rewardConfigs, aasmState } = task;
  const rewardConfig = rewardConfigs.find(
    isTypeFn('InGameCurrencyRewardConfig')
  );

  const isCompleted = aasmState === ManagerProgressionTaskState.COMPLETED;
  const isClaimed = aasmState === ManagerProgressionTaskState.CLAIMED;

  if (isCompleted || isClaimed) {
    return (
      <ClaimableContainer className={classNames({ claimed: isClaimed })}>
        <StyledLightedBezel
          color="var(--c-arena-primary)"
          bezelSize={16}
          radius={16}
          background="var(--c-nl-800)"
          borderColor="transparent"
          replaceBackground
        >
          <CardContent>
            {!!rewardConfig && (
              <AnyRewardConfig
                anyRewardConfig={rewardConfig}
                variant={Variant.CHIP}
              />
            )}
            <ContentWrapper>
              <Vertical gap={0.5}>
                <LabelL bold>{title}</LabelL>
                <BodyM color="var(--c-nd-600)">{description}</BodyM>
              </Vertical>
              {!!isCompleted && (
                <LinkOther as="div" style={{ width: '100%' }}>
                  <ClaimButton
                    task={task}
                    onClaim={onClaim}
                    loading={loading}
                  />
                </LinkOther>
              )}
            </ContentWrapper>
          </CardContent>
        </StyledLightedBezel>
      </ClaimableContainer>
    );
  }

  const filledValue = progress > 0 ? 1 : 0;

  return (
    <InProgressWrapper>
      <Vertical gap={1.5}>
        <Vertical gap={0.5}>
          <LabelL bold>{title}</LabelL>
          <BodyM color="var(--c-nd-600)">{description}</BodyM>
        </Vertical>
        <SBHorizontal gap={2}>
          <ProgressSection>
            <StyledProgress
              value={filledValue}
              max={1}
              color="var(--c-arena-primary)"
              bgColor="var(--c-nd-150-opaque)"
            />
            <Horizontal gap={0}>
              <LabelS bold>{progress}</LabelS>
              <LabelS color="var(--c-nd-600)">/</LabelS>
              <LabelS color="var(--c-nd-600)" bold>
                ∞
              </LabelS>
            </Horizontal>
          </ProgressSection>
          {!!rewardConfig && (
            <AnyRewardConfig
              anyRewardConfig={rewardConfig}
              variant={Variant.INLINE_CHIP}
            />
          )}
        </SBHorizontal>
      </Vertical>
    </InProgressWrapper>
  );
};

GenericTaskRow.fragments = {
  taskInterface: gql`
    fragment GenericTaskRow_taskInterface on TaskInterface {
      id
      title
      description
      progress
      aasmState
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ...ClaimButton_taskInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${ClaimButton.fragments.taskInterface}
  ` as TypedDocumentNode<GenericTaskRow_taskInterface>,
};
